// @flow

import { combineReducers } from 'redux'
import { createDataGridReducer, createFormReducer, DataGridDuck, formActions } from 'react-redux-components'

import { addSuccessNotification, addErrorNotification } from './notification-manager'
import authenticatedRequest from './authenticated-request'

import type { DataGridState, FormState } from 'react-redux-components'
import type { GetState, Dispatch, ReduxAction } from './index'
import type { NetError } from './authenticated-request'

// Action types
const ADD_USER_PENDING: 'users/ADD_USER_PENDING' = 'users/ADD_USER_PENDING'
const ADD_USER_SUCCESS: 'users/ADD_USER_SUCCESS' = 'users/ADD_USER_SUCCESS'
const ADD_USER_ERROR: 'users/ADD_USER_ERROR' = 'users/ADD_USER_ERROR'

const UPDATE_USER_PENDING: 'users/UPDATE_USER_PENDING' = 'users/UPDATE_USER_PENDING'
const UPDATE_USER_SUCCESS: 'users/UPDATE_USER_SUCCESS' = 'users/UPDATE_USER_SUCCESS'
const UPDATE_USER_ERROR: 'users/UPDATE_USER_ERROR' = 'users/UPDATE_USER_ERROR'

const RESET_PASSWORD_REQUEST: 'users/RESET_PASSWORD_REQUEST' = 'users/RESET_PASSWORD_REQUEST'
const RESET_PASSWORD_CANCEL: 'users/RESET_PASSWORD_CANCEL' = 'users/RESET_PASSWORD_CANCEL'

const DELETE_USER_REQUEST: 'users/DELETE_USER_REQUEST' = 'users/DELETE_USER_REQUEST'
const DELETE_USER_CANCEL: 'users/DELETE_USER_CANCEL' = 'users/DELETE_USER_CANCEL'

const DELETE_USER_PENDING: 'users/DELETE_USER_PENDING' = 'users/DELETE_USER_PENDING'
const DELETE_USER_SUCCESS: 'users/DELETE_USER_SUCCESS' = 'users/DELETE_USER_SUCCESS'
const DELETE_USER_ERROR: 'users/DELETE_USER_ERROR' = 'users/DELETE_USER_ERROR'

const SET_UI_MODE: 'users/SET_UI_MODE' = 'users/SET_UI_MODE'

type UIMode = 'list' | 'edit' | 'add'

type ACTION_ADD_USER_SUCCESS = {| type: typeof ADD_USER_SUCCESS |}
type ACTION_ADD_USER_PENDING = {| type: typeof ADD_USER_PENDING |}
type ACTION_ADD_USER_ERROR = {| type: typeof ADD_USER_ERROR, payload: NetError |}

type ACTION_UPDATE_USER_SUCCESS = {| type: typeof UPDATE_USER_SUCCESS |}
type ACTION_UPDATE_USER_PENDING = {| type: typeof UPDATE_USER_PENDING |}
type ACTION_UPDATE_USER_ERROR = {| type: typeof UPDATE_USER_ERROR, payload: NetError |}

type ACTION_RESET_PASSWORD_REQUEST = {| type: typeof RESET_PASSWORD_REQUEST, payload: string |}
type ACTION_RESET_PASSWORD_CANCEL = {| type: typeof RESET_PASSWORD_CANCEL |}

type ACTION_DELETE_USER_REQUEST = {| type: typeof DELETE_USER_REQUEST, payload: string |}
type ACTION_DELETE_USER_CANCEL = {| type: typeof DELETE_USER_CANCEL |}

type ACTION_DELETE_USER_SUCCESS = {| type: typeof DELETE_USER_SUCCESS |}
type ACTION_DELETE_USER_PENDING = {| type: typeof DELETE_USER_PENDING |}
type ACTION_DELETE_USER_ERROR = {| type: typeof DELETE_USER_ERROR, payload: NetError |}

type ACTION_SET_UI_MODE = {| type: typeof SET_UI_MODE, payload: UIMode |}

export type ManageUsersAction =
  | ACTION_SET_UI_MODE
  | ACTION_ADD_USER_PENDING
  | ACTION_ADD_USER_SUCCESS
  | ACTION_ADD_USER_ERROR
  | ACTION_UPDATE_USER_PENDING
  | ACTION_UPDATE_USER_SUCCESS
  | ACTION_UPDATE_USER_ERROR
  | ACTION_RESET_PASSWORD_REQUEST
  | ACTION_RESET_PASSWORD_CANCEL
  | ACTION_DELETE_USER_REQUEST
  | ACTION_DELETE_USER_CANCEL
  | ACTION_DELETE_USER_PENDING
  | ACTION_DELETE_USER_SUCCESS
  | ACTION_DELETE_USER_ERROR

const statuses = {
  NEW: 'NEW',
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
}

export type User = {|
  username: string,
  fullname?: string,
  email?: string,
  role?: string,
  password?: string,
|}
const userForm = {
  username: '',
  fullname: '',
  email: '',
  role: '',
}

type Status = 'NEW' | 'PENDING' | 'SUCCESS' | 'ERROR'

type UsersState_ = {|
  status: Status,
  deleteUserPending: string,
  resetPasswordPending: string,
  mode: UIMode,
|}
type UsersState = $Shape<UsersState_>
const initialState = {
  status: statuses.NEW,
  deleteUserPending: '',
  resetPasswordPending: '',
  mode: 'list',
}

const userReducer = (state: UsersState = initialState, action: ReduxAction): UsersState => {
  switch (action.type) {
    case UPDATE_USER_PENDING:
    case DELETE_USER_PENDING:
    case ADD_USER_PENDING:
      return { ...state, status: statuses.PENDING }
    case UPDATE_USER_SUCCESS:
    case DELETE_USER_SUCCESS:
    case ADD_USER_SUCCESS:
      return { ...state, status: statuses.SUCCESS }
    case UPDATE_USER_ERROR:
    case DELETE_USER_ERROR:
    case ADD_USER_ERROR:
      return { ...state, status: statuses.ERROR }

    case RESET_PASSWORD_REQUEST:
      return { ...state, resetPasswordPending: action.payload }
    case RESET_PASSWORD_CANCEL:
      return { ...state, resetPasswordPending: '' }

    case DELETE_USER_REQUEST:
      return { ...state, deleteUserPending: action.payload }
    case DELETE_USER_CANCEL:
      return { ...state, deleteUserPending: '' }

    case SET_UI_MODE:
      return { ...state, mode: action.payload }
    default:
      return state
  }
}

export type ManageUsersState = {
  ui: {| ...UsersState |},
  form: FormState,
  grid: DataGridState,
}
export const reducer = combineReducers({
  grid: createDataGridReducer('gridUsers'),
  form: createFormReducer('users.form', userForm),
  ui: userReducer,
})

export default reducer

//Creators
export const showUserForm = (user: User = userForm) => (dispatch: Dispatch, getState: GetState) => {
  dispatch(formActions.load('users.form', user))
  dispatch(DataGridDuck.creators.openSidebar('gridUsers'))
}
export const hideUserForm = () => (dispatch: Dispatch, getState: GetState) => {
  dispatch(formActions.reset('users.form'))
  dispatch(DataGridDuck.creators.closeSidebar('gridUsers'))
  dispatch(setUIMode('list'))
}

export const updateUser = (user: User) => async (dispatch: Dispatch, getState: GetState) => {
  dispatch({ type: UPDATE_USER_PENDING })
  try {
    let response = await authenticatedRequest.put('/api/users/' + user.username + '/update', user)
    dispatch({ type: UPDATE_USER_SUCCESS })
    dispatch(DataGridDuck.creators.closeSidebar('gridUsers'))
    dispatch(setUIMode('list'))
    if ('password' in user) {
      dispatch(addSuccessNotification('Password resettata con successo'))
    } else {
      dispatch(addSuccessNotification(response.data.message))
    }
    dispatch(DataGridDuck.creators.getRecords('gridUsers', {}, authenticatedRequest))
    dispatch(formActions.reset('users.form'))
  } catch (err) {
    dispatch({ type: UPDATE_USER_ERROR, payload: err })
    dispatch(formActions.setPending('users.form', false))
    dispatch(addErrorNotification(err.response.data.message))
  }
}

export const resetPasswordRequest = (username: string): ACTION_RESET_PASSWORD_REQUEST => ({
  type: RESET_PASSWORD_REQUEST,
  payload: username,
})
export const resetPasswordCancel = (): ACTION_RESET_PASSWORD_CANCEL => ({ type: RESET_PASSWORD_CANCEL })
export const resetPassword = (username: string) => (dispatch: Dispatch, getState: GetState) => {
  dispatch(formActions.setPending('users.form'))
  dispatch(resetPasswordCancel())
  dispatch(updateUser({ username, password: username }))
}

export const deleteUserRequest = (username: string): ACTION_DELETE_USER_REQUEST => ({
  type: DELETE_USER_REQUEST,
  payload: username,
})
export const deleteUserCancel = (): ACTION_DELETE_USER_CANCEL => ({ type: DELETE_USER_CANCEL })
export const deleteUser = (username: string) => async (dispatch: Dispatch, getState: GetState) => {
  dispatch({ type: DELETE_USER_PENDING })
  try {
    let response = await authenticatedRequest.delete('/api/users/' + encodeURIComponent(username) + '/delete')
    dispatch({ type: DELETE_USER_SUCCESS })
    dispatch(addSuccessNotification(response.data.message))
    dispatch(DataGridDuck.creators.getRecords('gridUsers', {}, authenticatedRequest))
  } catch (err) {
    dispatch({ type: DELETE_USER_ERROR, payload: err })
    dispatch(addErrorNotification(err.response.data.message))
  }
  dispatch(deleteUserCancel())
}

export const createUser = (user: User) => async (dispatch: Dispatch, getState: GetState) => {
  dispatch({ type: ADD_USER_PENDING })
  try {
    let response = await authenticatedRequest.post('/api/users/new', user)
    dispatch({ type: ADD_USER_SUCCESS })
    dispatch(DataGridDuck.creators.closeSidebar('gridUsers'))
    dispatch(setUIMode('list'))
    dispatch(addSuccessNotification(response.data.message))
    dispatch(DataGridDuck.creators.getRecords('gridUsers', {}, authenticatedRequest))
    dispatch(formActions.reset('users.form'))
  } catch (err) {
    dispatch({ type: ADD_USER_ERROR, payload: err })
    dispatch(formActions.setPending('users.form', false))
    dispatch(addErrorNotification(err.response.data.message))
  }
}

export const setUIMode = (mode: UIMode): ACTION_SET_UI_MODE => ({ type: 'users/SET_UI_MODE', payload: mode })



// WEBPACK FOOTER //
// ./src/store/manage-users.js